import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { CheckCircle } from 'react-bootstrap-icons';
import { fetchOrderConfirmation, clearCartAfterPayment } from '../services/api';
import FroggySpinner from '../components/common/FroggySpinner';
import { formatCurrency } from '../utils/currency';
import { formatDateLong } from '../utils/dateUtils';
import { buildAssetUrl, retryThenTile } from '../utils/imageResolver';
import { NO_PHOTO_SRC } from '../constants/noPhoto';
import { computeDeliveryDisplay } from '../utils/deliveryHelpers';
import ShippingTemperatureNotice from '../components/common/ShippingTemperatureNotice';
import { deliveryMethodOfOrder } from '../constants/shippingTemperatures';
import { renderVariationLines } from '../utils/variationHelpers';
import { useCart } from '../context/CartContext';
import { memberDisplayLabel } from '../components/MyStore/CreateLotModal';
// #68 named offers + the free-shipping line; shape notes live with the
// helpers (was a per-page copy until ShipmentDrawer became the 4th surface).
import { shippingDiscountOf, shippingOfferName, namedPromotions } from '../utils/promotionDisplay';
import '../styles/orders.css';
import '../styles/orderConfirmation.css';
import '../styles/cart.css'; // #90b shared .discount-breakdown classes

const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const cartClearAttempted = useRef(false);

  useEffect(() => {
    const loadOrderDetails = async () => {
      if (!orderId) {
        setError('No order ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const response = await fetchOrderConfirmation(orderId);
        
        // Handle both direct order data and wrapped response
        const orderData = response.order || response;
        
        if (!orderData || !orderData.id) {
          throw new Error('Invalid order data received from server');
        }
        
        setOrder(orderData);
        setError(null);
        
        // Safety net: Ensure cart is cleared for paid orders (only once per mount)
        if (orderData.payment_status === 'PAID' && !cartClearAttempted.current) {
          cartClearAttempted.current = true;
          try {
            const cartClearResult = await clearCartAfterPayment(orderId);
            
            if (cartClearResult.cleared) {
              
              // Also clear frontend cart state to be safe
              localStorage.removeItem('cartId');
              localStorage.removeItem('cartItems');
              
              try {
                await clearCart();
              } catch (cartError) {
                // Don't fail the page load for cart clearing issues
              }
            } else {
            }
          } catch (cartClearError) {
            // Don't fail the page load for cart clearing issues
          }
        }
      } catch (err) {
        
        // Provide specific error messages based on error type
        let errorMessage = 'Failed to load order details.';
        if (err.response?.status === 404) {
          errorMessage = 'Order not found. Please check your order number.';
        } else if (err.response?.status === 403) {
          errorMessage = 'You do not have permission to view this order.';
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadOrderDetails();
  }, [orderId, retryCount]);

  const handleBackToShop = () => {
    navigate('/');
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <FroggySpinner size="lg" label="Loading your order confirmation" />
      </Container>
    );
  }

  if (error || (!loading && !order)) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Unable to Load Order</Alert.Heading>
          <p>{error || 'Order data is not available'}</p>
          {retryCount < 3 && (
            <p>
              <small className="text-muted">
                If you just completed payment, please wait a moment and try again.
              </small>
            </p>
          )}
          <hr />
          <div className="d-flex justify-content-between">
            <div>
              {retryCount < 3 && (
                <button
                  type="button"
                  onClick={handleRetry}
                  className="ff-btn ff-btn--primary"
                  disabled={loading}
                >
                  {loading ? 'Retrying...' : 'Retry'}
                </button>
              )}
            </div>
            <button type="button" onClick={handleBackToShop} className="ff-btn ff-btn--secondary">
              Return to Shop
            </button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col lg={8}>
          {/* Success Header */}
          <Card className="mb-4 border-success oc-banner">
            <Card.Body className="text-center py-4">
              <CheckCircle size={64} className="text-success mb-3 oc-banner__icon" />
              <h1 className="text-success mb-2 oc-banner__title">Payment Confirmed</h1>
              <p className="lead mb-0">Thank you for your order!</p>
            </Card.Body>
          </Card>

          {/* Order Details */}
          <Card className="mb-4">
            <Card.Header>
              <h4 className="mb-0">Order Details</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p><strong>Order Number:</strong> {order.id}</p>
                  {order.paid_at && (
                    <p><strong>Payment Date:</strong> {new Date(order.paid_at).toLocaleString()}</p>
                  )}
                </Col>
                <Col md={6}>
                  <p><strong>Status:</strong> <span className="text-success oc-status oc-status--paid">{order.payment_status}</span></p>
                  {order.delivery_date && (
                    <p><strong>Expected Delivery:</strong> {formatDateLong(order.delivery_date)}</p>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Line Items */}
          <Card className="mb-4">
            <Card.Header>
              <h4 className="mb-0">Items Ordered</h4>
            </Card.Header>
            <Card.Body className="p-0">
              {order.items && order.items.length > 0 ? (
                order.items.map((item, index) => (
                  <div key={item.id || index} className="border-bottom p-3">
                    <Row className="align-items-center">
                      <Col md={2}>
                        {(() => {
                          const variation = item.product_variation;
                          const morph = variation?.morph;
                          const species = variation?.product?.species_id;
                          
                          // Full cascade hierarchy: variation → morph → species → placeholder
                          let imageUrl = null;
                          let source = 'placeholder';
                          
                          // Priority 1: Variation images
                          if (variation?.cover_display_url) {
                            imageUrl = buildAssetUrl(variation.cover_display_url);
                            source = 'variation.cover_display_url';
                          } else if (variation?.cover_thumb_url) {
                            imageUrl = buildAssetUrl(variation.cover_thumb_url);
                            source = 'variation.cover_thumb_url';
                          } else if (variation?.image_url) {
                            imageUrl = buildAssetUrl(variation.image_url);
                            source = 'variation.image_url';
                          }
                          // Priority 2: Morph images
                          else if (morph?.cover_display_url) {
                            imageUrl = buildAssetUrl(morph.cover_display_url);
                            source = 'morph.cover_display_url';
                          } else if (morph?.cover_thumb_url) {
                            imageUrl = buildAssetUrl(morph.cover_thumb_url);
                            source = 'morph.cover_thumb_url';
                          } else if (morph?.image_url) {
                            imageUrl = buildAssetUrl(morph.image_url);
                            source = 'morph.image_url';
                          }
                          // Priority 3: Species images
                          else if (species?.cover_display_url) {
                            imageUrl = buildAssetUrl(species.cover_display_url);
                            source = 'species.cover_display_url';
                          } else if (species?.cover_thumb_url) {
                            imageUrl = buildAssetUrl(species.cover_thumb_url);
                            source = 'species.cover_thumb_url';
                          } else if (species?.image_url) {
                            imageUrl = buildAssetUrl(species.image_url);
                            source = 'species.image_url';
                          }
                          
                          // Fallback to placeholder
                          const finalUrl = imageUrl || NO_PHOTO_SRC;
                          
                          return (
                            <img 
                              src={finalUrl}
                              alt={item.name}
                              className="img-fluid rounded"
                              data-no-photo={imageUrl ? undefined : '1'}
                              onError={imageUrl ? retryThenTile : undefined}
                              style={{ maxHeight: '60px', objectFit: imageUrl ? 'cover' : 'contain' }}
                            />
                          );
                        })()}
                      </Col>
                      <Col md={6}>
                        <h6 className="mb-1">{item.name || 'Product'}</h6>
                        {renderVariationLines(item.product_variation)}
                        {/* I-02d — lot lines list their members;
                            I-02d.2 pool bundles show Group of N (no
                            members — pulled from stock). */}
                        {item.lot?.lot_type === 'pool_group' ? (
                          <small className="text-muted d-block">
                            Group of {item.lot.quantity} — pulls from available stock
                          </small>
                        ) : item.lot?.members?.length > 0 && (
                          <small className="text-muted d-block">
                            {/* Cowork F1 — name or #SHORTID · ~age */}
                            {item.lot.members.map((m) => {
                              const glyph = m.gender === 0 ? '♂' : m.gender === 1 ? '♀' : '?';
                              return `${glyph} ${memberDisplayLabel(m)}`;
                            }).join(' · ')}
                          </small>
                        )}
                        <small className="text-muted">Unit Price: {formatCurrency(item.unit_price)}</small>
                      </Col>
                      <Col md={2} className="text-center">
                        <strong>Qty: {item.quantity}</strong>
                      </Col>
                      <Col md={2} className="text-end">
                        <strong>{formatCurrency(item.line_total)}</strong>
                      </Col>
                    </Row>
                  </div>
                ))
              ) : (
                <div className="p-3 text-muted">No items found</div>
              )}
            </Card.Body>
          </Card>

          {/* Order Totals & Shipping */}
          <Card className="mb-4">
            <Card.Header>
              <h4 className="mb-0">Order Summary</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  {/* Shipping Information */}
                  <h6>Shipping Method</h6>
                  <p className="mb-1">
                    <strong>{order.shipping_summary?.method || 'Standard Shipping'}</strong>
                  </p>
                  {order.shipping_summary?.carrier && (
                    <p className="mb-1">
                      Carrier: {order.shipping_summary.carrier} - {order.shipping_summary.service}
                    </p>
                  )}
                  {order.shipping_summary?.destination_type === 'pickup' && (
                    <p className="mb-0">
                      <small className="text-muted">{computeDeliveryDisplay(order).label} (details via email)</small>
                    </p>
                  )}
                </Col>
                <Col md={6}>
                  {/* Order Totals */}
                  <div className="d-flex justify-content-between mb-2">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(order.subtotal)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Shipping:</span>
                    <span>{formatCurrency(order.shipping_total)}</span>
                  </div>

                  {shippingDiscountOf(order) > 0 && (
                    <div className="d-flex justify-content-between mb-2">
                      <span style={{ color: 'var(--ff-semantic-success)' }}>
                        Shipping discount{shippingOfferName(order) ? ` (${shippingOfferName(order)})` : ''}:
                      </span>
                      <span style={{ color: 'var(--ff-semantic-success)' }}>
                        -{formatCurrency(shippingDiscountOf(order))}
                      </span>
                    </div>
                  )}


                  {/* Discount Breakdown */}
                  {order.total_discount && parseFloat(order.total_discount) > 0 && (
                    <>
                      {order.auto_discount && parseFloat(order.auto_discount) > 0 && (
                        <div className="d-flex justify-content-between mb-2">
                          <span style={{ color: 'var(--ff-semantic-success)' }}>Automatic discount:</span>
                          <span style={{ color: 'var(--ff-semantic-success)' }}>-{formatCurrency(order.auto_discount)}</span>
                        </div>
                      )}
                      {order.promo_code_discount && parseFloat(order.promo_code_discount) > 0 && (
                        <div className="d-flex justify-content-between mb-2">
                          <span style={{ color: 'var(--ff-semantic-success)' }}>Promo code:</span>
                          <span style={{ color: 'var(--ff-semantic-success)' }}>-{formatCurrency(order.promo_code_discount)}</span>
                        </div>
                      )}
                      {/* #68 — name the offers behind those amounts, same
                          bullet treatment the cart and checkout use. */}
                      {(() => {
                        const offers = namedPromotions(order.applied_promotions);
                        if (!offers.length) return null;
                        return (
                          <div className="discount-breakdown__promos">
                            {offers.map((promo, idx) => (
                              <div key={idx} className="discount-breakdown__promo-line">
                                • {promo.name}
                                {Number.isFinite(promo.amount) && (
                                  <span className="discount-breakdown__amount">
                                    {' '}−{formatCurrency(promo.amount)}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </>
                  )}

                  <hr />
                  <div className="d-flex justify-content-between">
                    <strong>Total Paid:</strong>
                    <strong className="text-success">{formatCurrency(order.order_total)}</strong>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Next Steps */}
          <Card className="mb-4">
            <Card.Body>
              <h5 className="order-next-steps-title">What happens next?</h5>
              <ul className="order-next-steps-list">
                <li className="order-next-steps-item step-confirmation">Order confirmation & scheduling email to coordinate delivery details</li>
                <li className="order-next-steps-item step-preparation">We'll prepare your animals for safe transport</li>
                <li className="order-next-steps-item step-shipping">Tracking information once your order ships</li>
                <li className="order-next-steps-item step-pickup">Pickup instructions (if applicable)</li>
              </ul>
            </Card.Body>
          </Card>

          {/* Shipping Policy Notice */}
          <Card className="mb-4 border-info">
            <Card.Body>
              <h6 className="order-shipping-notice-title">🌡️ Important Shipping Information</h6>
              {/* Fix batch 42: the method the customer CHOSE, never a blanket "Residential" */}
              <ShippingTemperatureNotice
                method={deliveryMethodOfOrder(order)}
                lineClassName="order-shipping-notice-text"
              />
              <p className="order-shipping-notice-text">
                {/* a link is --ff-link, the one link colour (R3, 2026-10-07) */}
                <button
                  type="button"
                  className="ff-btn ff-btn--link p-0 text-decoration-underline"
                  onClick={() => navigate('/shipping-policy')}
                >
                  View complete shipping policy
                </button>{' '}
                • Questions? Email{' '}
                <a href="mailto:support@feelingfroggy.llc" className="text-decoration-underline oc-inline-link">
                  support@feelingfroggy.llc
                </a>
              </p>
            </Card.Body>
          </Card>

          {/* Action Buttons */}
          <div className="text-center">
            {/* Design System v1 §2.2: the page's ONE primary, at the
                family's 36px — no page-local fill, no inline size */}
            <button
              type="button"
              onClick={handleBackToShop}
              className="ff-btn ff-btn--primary oc-continue-btn"
            >
              Continue Shopping
            </button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default OrderConfirmationPage;